import * as React from 'react';
import styled from 'styled-components';
import SearchIcon from '@material-ui/icons/Search';
import DescriptionIcon from '@material-ui/icons/Description';
import { getCustomActivities } from '../../api/activities';
import { Loader } from '../../atoms';
import { DEFAULT_DATE_FORMAT, GET_ALL_QUERY_PARAMS } from '../../constants';
import { formatDateTime } from '../../utils/formatDate';

type StatusTone = 'success' | 'warning' | 'info' | 'neutral' | 'danger';

const toneByStatus = (raw: string): StatusTone => {
  const s = (raw || '').trim().toLowerCase();
  if (!s) return 'neutral';
  if (s.includes('pass') || s === 'approved' || s.includes('completed')) return 'success';
  if (s.includes('fail') || s.includes('rejected') || s.includes('cancelled') || s.includes('denied')) return 'danger';
  if (s.includes('not applicable')) return 'neutral';
  if (s.includes('scheduled') || s.includes('progress')) return 'info';
  return 'warning';
};

type InspectionRow = {
  id: number;
  title: string;
  type: string;
  status: string;
  inspector: string;
  contact: string;
  assignedTo: string;
  inspectionDate: string;
  followUp: string;
  updatedAt: string;
  createdAt: string;
  information: string;
  report: string | null;
  visibleFilename: string;
  isEmergency: boolean;
};

const fullName = (first?: string, last?: string) => [first, last].filter(Boolean).join(' ');

const contactDisplay = (insp: any) => {
  const name = fullName(insp?.contactFirstName, insp?.contactLastName);
  const company = insp?.companyName || insp?.parentCompany || '';
  return [name, company].filter(Boolean).join(' — ');
};

const formatMaybeDate = (value: string | undefined) =>
  value ? formatDateTime(value, DEFAULT_DATE_FORMAT) : '';

const mapInspection = (insp: any): InspectionRow => ({
  id: insp.id,
  title: insp?.activityTitleName || '',
  type: insp?.activityTypeName || '',
  status: insp?.statusName || '',
  inspector: fullName(insp?.inspectorFirstName, insp?.inspectorLastName),
  contact: contactDisplay(insp),
  assignedTo: fullName(insp?.assignedToFirstName, insp?.assignedToLastName),
  inspectionDate: formatMaybeDate(insp?.inspectionDate),
  followUp: formatMaybeDate(insp?.followUp),
  updatedAt: formatMaybeDate(insp?.updatedAt || insp?.completedAt),
  createdAt: formatMaybeDate(insp?.createdAt),
  information: insp?.information || '',
  report: insp?.report || null,
  visibleFilename: insp?.visibleFilename || '',
  isEmergency: !!insp?.isEmergency,
});

const buildReportUrl = (raw: string | null) => {
  if (!raw) return '';
  const wrapped = raw.match(/\/media\/(https?%3A\/\/.*)$/i);
  if (wrapped) return decodeURIComponent(wrapped[1]);
  return raw;
};

interface Props {
  permit: number;
  onCountChange?: (count: number) => void;
  onDetailsLoad?: (detail: any) => void;
}

const InspectionCardList: React.FC<Props> = ({ permit, onCountChange, onDetailsLoad }) => {
  const [rows, setRows] = React.useState<InspectionRow[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    getCustomActivities({
      ...GET_ALL_QUERY_PARAMS,
      permit_id: permit,
      ordering: null,
      event_type: 'Inspection',
    })
      .then(res => {
        if (cancelled) return;
        // Bubble the first raw row up for the permit-level header block
        // (project/permit name, address, number) — same data, no extra request.
        if (onDetailsLoad && res?.data?.results?.[0]) {
          onDetailsLoad(res.data.results[0]);
        }
        const results = (res?.data?.results || []).map(mapInspection);
        results.sort((a, b) => {
          const aTime = a.inspectionDate ? new Date(a.inspectionDate).getTime() : 0;
          const bTime = b.inspectionDate ? new Date(b.inspectionDate).getTime() : 0;
          return bTime - aTime;
        });
        setRows(results);
        if (onCountChange) onCountChange(res?.data?.highestCount ?? results.length);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [permit]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      [
        r.title,
        r.type,
        r.status,
        r.inspector,
        r.contact,
        r.assignedTo,
        r.inspectionDate,
        r.followUp,
        r.information,
        r.visibleFilename,
        String(r.id),
      ]
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [rows, search]);

  return (
    <Root>
      <Toolbar>
        <SearchWrapper>
          <SearchIcon style={{ fontSize: 18 }} />
          <SearchInput
            type="text"
            placeholder="Search by inspector, contact, status…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </SearchWrapper>
        <ResultMeta>
          {isLoading ? '' : `${filtered.length} of ${rows.length} shown`}
        </ResultMeta>
      </Toolbar>

      <ListWrapper>
        {isLoading && (
          <LoaderWrap>
            <Loader />
          </LoaderWrap>
        )}
        {!isLoading && filtered.length === 0 && (
          <EmptyState>
            {rows.length === 0 ? 'No inspections on this permit yet.' : 'No inspections match your search.'}
          </EmptyState>
        )}
        {!isLoading &&
          filtered.map(insp => {
            const tone = toneByStatus(insp.status);
            const reportUrl = buildReportUrl(insp.report);
            return (
              <Card key={insp.id} $emergency={insp.isEmergency}>
                <CardHeader>
                  <HeaderText>
                    <Eyebrow>
                      Inspection #{insp.id}
                      {insp.type ? ` · ${insp.type}` : ''}
                    </Eyebrow>
                    <Title>{insp.title || '—'}</Title>
                  </HeaderText>
                  <HeaderRight>
                    {insp.isEmergency && <EmergencyChip>Emergency</EmergencyChip>}
                    <StatusChip $tone={tone}>{insp.status || '—'}</StatusChip>
                  </HeaderRight>
                </CardHeader>

                <CardBody>
                  <DetailGrid>
                    <Field>
                      <FieldLabel>Inspection Date</FieldLabel>
                      <FieldValue>{insp.inspectionDate || '—'}</FieldValue>
                    </Field>
                    <Field>
                      <FieldLabel>Inspector</FieldLabel>
                      <FieldValue>{insp.inspector || '—'}</FieldValue>
                    </Field>
                    <Field>
                      <FieldLabel>Activity Contact</FieldLabel>
                      <FieldValue>{insp.contact || '—'}</FieldValue>
                    </Field>
                    <Field>
                      <FieldLabel>Follow Up</FieldLabel>
                      <FieldValue>{insp.followUp || '—'}</FieldValue>
                    </Field>
                    <Field>
                      <FieldLabel>Updated</FieldLabel>
                      <FieldValue>{insp.updatedAt || '—'}</FieldValue>
                    </Field>
                    <Field>
                      <FieldLabel>Assigned To</FieldLabel>
                      <FieldValue>{insp.assignedTo || '—'}</FieldValue>
                    </Field>
                  </DetailGrid>

                  <FullWidthField>
                    <FieldLabel>Inspection Report</FieldLabel>
                    {reportUrl ? (
                      <ReportLink href={reportUrl} target="_blank" rel="noreferrer">
                        <DescriptionIcon style={{ fontSize: 16 }} />
                        <span>{insp.visibleFilename || 'View report'}</span>
                      </ReportLink>
                    ) : (
                      <FieldValue>—</FieldValue>
                    )}
                  </FullWidthField>

                  {insp.information && (
                    <FullWidthField>
                      <FieldLabel>Information</FieldLabel>
                      <FieldValue>{insp.information}</FieldValue>
                    </FullWidthField>
                  )}
                </CardBody>
              </Card>
            );
          })}
      </ListWrapper>
    </Root>
  );
};

export default InspectionCardList;

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif`;

const tonePalette: Record<StatusTone, { bg: string; fg: string; border: string }> = {
  success: { bg: 'var(--success-50, #e8f6ee)', fg: 'var(--success-700, #196a3b)', border: 'var(--success-100, #c5e7d2)' },
  warning: { bg: 'var(--warning-50, #fff6e0)', fg: 'var(--warning-700, #8a5f05)', border: 'var(--warning-100, #ffe7ac)' },
  info: { bg: 'var(--info-50, #e8f2fd)', fg: 'var(--info-600, #1e64bf)', border: 'var(--primary-100, #cfe6f3)' },
  danger: { bg: 'var(--danger-50, #fdecec)', fg: 'var(--danger-700, #9a2523)', border: 'var(--danger-100, #f9c9c9)' },
  neutral: { bg: 'var(--neutral-100)', fg: 'var(--neutral-700)', border: 'var(--border)' },
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: ${FONT_STACK};
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const SearchWrapper = styled.label`
  flex: 1 1 280px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  color: var(--neutral-500);

  &:focus-within {
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
    color: var(--primary-600);
  }
`;

const SearchInput = styled.input`
  flex: 1;
  border: 0;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: 0.875rem;
  color: var(--mainText);

  &::placeholder {
    color: var(--neutral-400);
  }
`;

const ResultMeta = styled.span`
  font-size: 0.8125rem;
  color: var(--neutral-500);
  white-space: nowrap;
`;

const ListWrapper = styled.div`
  display: block;

  > * + * {
    margin-top: 14px;
  }
`;

const LoaderWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

const EmptyState = styled.div`
  padding: 48px 16px;
  text-align: center;
  color: var(--neutral-500);
  font-size: 0.875rem;
  border: 1px dashed var(--border);
  border-radius: 10px;
  background: var(--neutral-50);
`;

const Card = styled.div<{ $emergency?: boolean }>`
  display: block;
  background: var(--neutral-0);
  border: 1px solid ${({ $emergency }) => ($emergency ? 'var(--danger-100, #f9c9c9)' : 'var(--border)')};
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${({ $emergency }) => ($emergency ? 'var(--danger-500, #d44b3f)' : 'var(--neutral-400)')};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px 18px;
  background: var(--neutral-50);
  border-bottom: 1px solid var(--border);
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  min-height: 56px;
  box-sizing: border-box;

  @media only screen and (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 12px 14px;
  }
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1 1 auto;
`;

const HeaderRight = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  flex: 0 0 auto;
`;

const Eyebrow = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const Title = styled.span`
  font-size: 1rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.35;
  word-break: break-word;
`;

const StatusChip = styled.span<{ $tone: StatusTone }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1.4;
  white-space: nowrap;
  background: ${({ $tone }) => tonePalette[$tone].bg};
  color: ${({ $tone }) => tonePalette[$tone].fg};
  border: 1px solid ${({ $tone }) => tonePalette[$tone].border};
`;

const EmergencyChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: var(--danger-50, #fdecec);
  color: var(--danger-700, #9a2523);
  border: 1px solid var(--danger-100, #f9c9c9);
`;

const CardBody = styled.div`
  display: block;
  padding: 16px 18px 18px;
  background: var(--neutral-0);
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;

  > * + * {
    margin-top: 14px;
  }

  @media only screen and (max-width: 600px) {
    padding: 14px;

    > * + * {
      margin-top: 12px;
    }
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px 24px;

  @media only screen and (max-width: 840px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 16px;
  }

  @media only screen and (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const FullWidthField = styled(Field)`
  grid-column: 1 / -1;
  border-top: 1px solid var(--border);
  padding-top: 12px;
`;

const FieldLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const FieldValue = styled.span`
  font-size: 0.875rem;
  color: var(--mainText);
  line-height: 1.45;
  word-break: break-word;
  white-space: pre-wrap;
`;

const ReportLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--primary-700);
  text-decoration: none;
  transition: color 0.15s ease;

  &:hover {
    color: var(--primary-800, #1e6695);
    text-decoration: underline;
  }

  svg {
    color: var(--neutral-500);
  }

  &:hover svg {
    color: var(--primary-700);
  }
`;
